import { AntSwitch, GeoAddress, Icons, Progress } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { useAProgress } from 'hooks/useAProgress'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { USCompany } from 'modules/domain/company/types'
import { Address } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { CommonFields } from '../CommonFields'

const SwitchesWrapper = styled.div`
  display: flex;
  column-gap: 24px;
`

const SwitchFormWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
`

const MapControlWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`

const createAddressDto = (address: GeoAddress, companyId: string) => {
  const { coords, ...otherParams } = address
  const [latitude, longitude] = coords

  return { latitude, longitude, company: companyId, ...otherParams }
}

type Props = {
  onClose: () => void
  company: USCompany
  onSuccess: refetchFunc | (() => void)
}

export const USEditCompanyModal: FC<Props> = ({ onClose, company, onSuccess }) => {
  const { t } = useTranslation(['company', 'common', 'address'])
  const notify = useNotification()
  const [addressEditInProgress, setAddressEditInProgress] = useState(false)
  const isIrelySyncedSeller = company.is_synced_with_accounting_as_seller
  const isIrelySyncedBuyer = company.is_synced_with_accounting_as_customer

  const initAddress: GeoAddress | null = company.main_address
    ? {
        coords: [company.main_address.latitude, company.main_address.longitude],
        address: company.main_address.address,
        geoObject: {},
      }
    : null

  const [address, setAddress] = useState<GeoAddress | null>(initAddress)

  const { formProgress, formHandler } = useAFormHandler()

  const [saveAddressProgress, saveAddress] = useAProgress<Address>(
    () =>
      apiClient.post<Address>(
        endpoints.companyAddress(company.id),
        createAddressDto(address as GeoAddress, company.id),
      ),
    {
      eventName: 'addAddress',
      onSuccess: (res) => {
        formik.setFieldValue('main_address', res.id)
        notify(Progress.SUCCESS, { title: t('address:newAddressSuccess') })
        setAddressEditInProgress(false)
      },
      onError: (error) => {
        const { errors } = error as RequestError
        formik.setErrors(errors)

        notify(Progress.ERROR, { title: errors.main_address })
      },
    },
  )

  const formik = useFormik({
    initialValues: {
      name: company.name,
      is_significant: company.is_significant,
      main_address: company.main_address?.id,
      responsible_coordinator: company.responsible_coordinator?.id,
      inn: company.inn,
      commercial: company.commercial,
      responsible_seller: company.responsible_seller?.id,
      responsible_customer: company.responsible_customer?.id,
      merged_responsible: null,
      is_synced_with_accounting_as_customer: company.is_synced_with_accounting_as_customer,
      is_synced_with_accounting_as_seller: company.is_synced_with_accounting_as_seller,
      parent: typeof company.parent === 'object' ? company.parent?.id : company.parent,
    },
    enableReinitialize: true,

    onSubmit: formHandler(
      (val) => {
        const { merged_responsible, ...actualFormValues } = val

        if (merged_responsible) {
          actualFormValues.responsible_seller = merged_responsible
          actualFormValues.responsible_customer = merged_responsible
        }
        return apiClient.put(endpoints.company(company.id), actualFormValues)
      },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
        onError: (err) => {
          formik.setErrors(err.errors)
        },
      },
    ),
  })

  const closeMapHandler = () => {
    setAddress(initAddress)
    setAddressEditInProgress(false)
  }

  return (
    <ResponsiveModal
      id="editCompany"
      onClose={onClose}
      title={t('form.edit')}
      progress={formProgress}
      onConfirm={formik.submitForm}
      disabled={addressEditInProgress}
    >
      <SharedModalContent>
        <SharedModalInputs>
          <CommonFields.NameCompany formik={formik} withSignificant />

          <CommonFields.AddressField
            address={address}
            handlerMap={(val) => setAddress(val)}
            formik={formik}
            disabled={!addressEditInProgress}
            required
          >
            {addressEditInProgress ? (
              <MapControlWrapper>
                <AButton
                  id="sendAddressData"
                  color="secondary"
                  Icon={Icons.IconDone}
                  size="big"
                  disabled={!address}
                  onClick={saveAddress}
                  progress={saveAddressProgress}
                />
                <AButton id="closeMap" color="secondary" Icon={Icons.IconClose} size="big" onClick={closeMapHandler} />
              </MapControlWrapper>
            ) : (
              <AButton
                id="openMap"
                color="secondary"
                Icon={Icons.IconEdit}
                size="big"
                onClick={() => setAddressEditInProgress(true)}
              />
            )}
          </CommonFields.AddressField>
          <CommonFields.ResponsibleField formik={formik} />
          <CommonFields.PersonalNumber formik={formik} />
          <CommonFields.CommercialField formik={formik} />

          <CommonFields.USCompanyResponsibleUsers company={company} formik={formik} isEdit />

          <HrWithHeader text={t('iRelySynchronization')} />
          <SwitchesWrapper>
            <SwitchFormWrapper>
              <AntSwitch
                checked={formik.values.is_synced_with_accounting_as_seller}
                onChange={(value) => formik.setFieldValue('is_synced_with_accounting_as_seller', value)}
                disabled={
                  (!formik.values.responsible_seller && !formik.values.merged_responsible) || isIrelySyncedSeller
                }
              />
              {t('irelySeller')}
            </SwitchFormWrapper>
            <SwitchFormWrapper>
              <AntSwitch
                checked={formik.values.is_synced_with_accounting_as_customer}
                onChange={(value) => formik.setFieldValue('is_synced_with_accounting_as_customer', value)}
                disabled={
                  (!formik.values.responsible_customer && !formik.values.merged_responsible) || isIrelySyncedBuyer
                }
              />
              {t('irelyBuyer')}
            </SwitchFormWrapper>
          </SwitchesWrapper>
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
